import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";
import { EmployeeUnitExtractRow } from "../../utils/employeeUnitsExcel";

export type DailyRedesignRow = {
  date: string;
  lab_name: string;
  case_name: string;
  allocated_to: string;
  qc_capt: string;
  qc_on_qc: string;
  unit: number | null;
  grade: number | null;
  category: string;
  original_designer: string;
  original_qc: string;
  original_qc_on_qc: string;
  internal_external: "I" | "E";
  rd_no: string;
  discussion: string;
  year_month: string;
  category_designer: string;
  notes: string;
};

export type RdPercentageLabRow = {
  lab: string;
  total_cases: number;
  total_redesigns: number;
  rd_pct: number | null;
};

export type RdPercentageReport = {
  date: string;
  window: { from: string; to: string };
  counts: {
    live: number;
    total_redesigns: number;
    client: number;
    internal: number;
  };
  percentages: {
    total_rd_of_live: number | null;
    client_of_live: number | null;
    internal_of_live: number | null;
  };
  labs: RdPercentageLabRow[];
};

export type RdPercentageSnapshot = RdPercentageReport & {
  key: string;
  label: string;
  fromDate: string;
  toDate: string;
};

export type LiveRedesignReport = {
  snapshots: RdPercentageSnapshot[];
};

export type LiveProductivityEmployee = {
  emp_name: string;
  role: string;
  design_unit_live: number;
  qc_unit_live: number;
  live_cases: number;
  del_unit_live: number;
  total_live: number;
  design_unit_redesign: number;
  qc_unit_redesign: number;
  rd_cases: number;
  total_redesign: number;
  all_total: number;
};

export type LiveProductivityCase = {
  kind: "live" | "rd";
  case_id: string;
  design_unit: number;
  qc_unit: number;
  del_unit: number;
  designed: string;
  qc2: string;
  unit_by: string;
};

export type LiveProductivityReport = {
  date: string;
  window: { from: string; to: string };
  live_cases: number;
  rd_cases: number;
  live_design_units: number;
  live_qc_units: number;
  rd_design_units: number;
  rd_qc_units: number;
  live_delivery_units: number;
  rd_delivery_units: number;
  live_units_all_marked: boolean;
  unmarked_live_case_ids: string[];
  cases: LiveProductivityCase[];
  employees: LiveProductivityEmployee[];
};

export type CasesCameSnapshot = {
  label: string;
  fromDate: string;
  toDate: string;
  window: { from: string; to: string };
  live_cases: number;
  live_units: number;
  external_rd_cases: number;
  external_rd_units: number;
  internal_rd_cases: number;
  internal_rd_units: number;
  rd_cases: number;
  rd_units: number;
  all_cases: number;
  all_units: number;
};

export type BillingDataRow = {
  case_id: string;
  creation_time: string;
  tooth_numbers: string;
  billing_category: string;
  delivery_unit: number;
  model: string;
};

export type CaseLifecycleRow = {
  case_id: string;
  live_case_id: string;
  kind: "live" | "redesign" | "internal_redesign";
  created_at: string | null;
  delivery_units: number | null;
  delivery_units_marked: boolean;
  design_unit: number | null;
  category: string | null;
  allocated_by: string | null;
  allocated_at: string | null;
  qc1_by: string | null;
  qc1_at: string | null;
  designed_by: string | null;
  designer_units: Record<string, number> | null;
  model_by: string | null;
  model_units: Record<string, number> | null;
  designed_at: string | null;
  qc2_by: string | null;
  qc2_at: string | null;
  upload_by: string | null;
  upload_at: string | null;
  upload_bg: "green" | "red" | null;
  has_reference: boolean;
  filled_order_patients?: Array<{
    patient: string;
    name: string;
    units: number;
    marked: boolean;
  }> | null;
};

export type CaseLifecycleReport = {
  date: string;
  window: { from: string; to: string };
  count: number;
  rows: CaseLifecycleRow[];
};

export type CaseLifecycleHistory = {
  live_case_id: string;
  count: number;
  rows: CaseLifecycleRow[];
};

interface DataState {
  allLiveAndRedesign: any[];
  allAllocations: any[];
  allFullDayCases: any[];
  labWiseCases: any[];
  labWiseRedesigns: any[];
  employeeUnits: EmployeeUnitExtractRow[];
  markedUnits: Record<string, unknown>[];
  dailyRedesigns: DailyRedesignRow[];
  liveProductivity: LiveProductivityReport | null;
  rdPercentage: RdPercentageReport | null;
  casesCame: CasesCameReport | null;
  billingData: BillingDataRow[];
  caseLifecycleReport: CaseLifecycleReport | null;
  caseLifecycleHistory: CaseLifecycleHistory | null;
  loading: boolean;
  error: string | null;
}

const initialState: DataState = {
  allLiveAndRedesign: [],
  allAllocations: [],
  allFullDayCases: [],
  labWiseCases: [],
  labWiseRedesigns: [],
  employeeUnits: [],
  markedUnits: [],
  dailyRedesigns: [],
  liveProductivity: null,
  rdPercentage: null,
  casesCame: null,
  billingData: [],
  caseLifecycleReport: null,
  caseLifecycleHistory: null,
  loading: false,
  error: null,
};


// FETCH LIVE + REDESIGNS TOKEN WISE
export const fetchAllLiveAndRedesignsByMonth = createAsyncThunk(
  "data/fetchAllLiveAndRedesignsByMonth",
  async (
    { month, year }: { month: number; year: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/api/data/allCases`,
        { month, year },
        { withCredentials: true }
      );

      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// FETCH LAB WISE REDESIGNS BY MONTH
export const fetchLabWiseRedesignsByMonth = createAsyncThunk(
  "data/fetchLabWiseRedesignsByMonth",
  async (
    {
      lab,
      month,
      year,
      categories,
      types,
    }: {
      lab: string;
      month: number;
      year: number;
      categories?: string[];
      types?: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/api/data/redesigns`,
        { lab, month, year, categories, types },
        { withCredentials: true }
      );

      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
export const fetchAllAllocationByMonth = createAsyncThunk(
  "data/fetchAllAllocationByMonth",
  async ({ month, year }: { month: number; year: number }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/api/data/allocation`,
        { month, year },
        { withCredentials: true }
      );
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// FETCH FULL DAY CASES
export const fetchAllFullDay = createAsyncThunk(
  "data/fetchAllFullDay",
  async (
    {
      date,
      month,
      year,
    }: {
      date: number;
      month: number;
      year: number;
    },
    { rejectWithValue }
  ) => {
    try {

      const formattedDate = `${year}-${String(month).padStart(2, "0")}-${String(date).padStart(2, "0")}`;

      const res = await api.post(
        `/api/data/fullDayCases`,
        {
          date: formattedDate,
        },
        { withCredentials: true }
      );

      return res.data.data;

    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// FETCH LAB WISE CASES BY MONTH
export const fetchLabWiseCasesByMonth = createAsyncThunk(
  "data/fetchLabWiseCasesByMonth",
  async (
    { lab, month, year }: { lab: string; month: number; year: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/api/data/labCases`,
        { lab, month, year },
        { withCredentials: true }
      );

      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchEmployeeUnits = createAsyncThunk(
  "data/fetchEmployeeUnits",
  async (
    { fromDate, toDate }: { fromDate: string; toDate: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/api/data/employeeUnits`,
        { fromDate, toDate },
        { withCredentials: true }
      );
      return (res.data.data?.employees || []) as EmployeeUnitExtractRow[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchMarkedUnitsExport = createAsyncThunk(
  "data/fetchMarkedUnitsExport",
  async (
    {
      fromDate,
      toDate,
      kind = "both",
    }: { fromDate: string; toDate: string; kind?: "both" | "live" | "redesign" },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        "/units/delivery-units/export-marked",
        { from: fromDate, to: toDate, kind },
        { timeout: 120000, withCredentials: true }
      );
      return (res.data.data?.cases || []) as Record<string, unknown>[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message
      );
    }
  }
);

export const fetchDailyRedesigns = createAsyncThunk(
  "data/fetchDailyRedesigns",
  async ({ date }: { date: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/api/data/dailyRedesigns",
        { date },
        { timeout: 120000, withCredentials: true },
      );
      return (res.data.data?.rows || []) as DailyRedesignRow[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchLiveProductivity = createAsyncThunk(
  "data/fetchLiveProductivity",
  async ({ date }: { date: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/api/data/liveProductivity",
        { date },
        { timeout: 120000, withCredentials: true },
      );
      return (res.data.data || null) as LiveProductivityReport | null;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchRdPercentage = createAsyncThunk(
  "data/fetchRdPercentage",
  async ({ date }: { date: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/api/data/rdPercentage",
        { date },
        { timeout: 120000, withCredentials: true },
      );
      return res.data.data as RdPercentageReport;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchLiveRedesignReport = createAsyncThunk(
  "data/fetchLiveRedesignReport",
  async (
    payload: {
      ranges: Array<{
        key: string;
        label: string;
        fromDate: string;
        toDate: string;
      }>;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post("/api/data/liveRedesignReport", payload, {
        timeout: 180000,
        withCredentials: true,
      });
      return res.data.data as LiveRedesignReport;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchCasesCame = createAsyncThunk(
  "data/fetchCasesCame",
  async (
    payload: { date?: string; fromDate?: string; toDate?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post("/api/data/casesCame", payload, {
        timeout: 120000,
        withCredentials: true,
      });
      return res.data.data as CasesCameReport;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchBillingDataByMonth = createAsyncThunk(
  "data/fetchBillingDataByMonth",
  async (
    { month, year }: { month: number; year: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post(
        "/api/data/billingData",
        { month, year },
        { timeout: 120000, withCredentials: true },
      );
      return (res.data.data || []) as BillingDataRow[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchCaseLifecycleReport = createAsyncThunk(
  "data/fetchCaseLifecycleReport",
  async ({ date }: { date: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/api/data/caseLifecycleReport",
        { date },
        { timeout: 180000, withCredentials: true },
      );
      return res.data.data as CaseLifecycleReport;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

export const fetchCaseLifecycleHistory = createAsyncThunk(
  "data/fetchCaseLifecycleHistory",
  async ({ live_case_id }: { live_case_id: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/api/data/caseLifecycleHistory",
        { live_case_id },
        { timeout: 120000, withCredentials: true },
      );
      return res.data.data as CaseLifecycleHistory;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || err.message,
      );
    }
  },
);

const dataExtractSlice = createSlice({
  name: "data",
  initialState,
  reducers: {},

  extraReducers: (builder) => {

    // LIVE + REDESIGNS
    builder
      .addCase(fetchAllLiveAndRedesignsByMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllLiveAndRedesignsByMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.allLiveAndRedesign = action.payload;
      })
      .addCase(fetchAllLiveAndRedesignsByMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // ALLOCATION BY MONTH
    builder
      .addCase(fetchAllAllocationByMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAllocationByMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.allAllocations = action.payload;
      })
      .addCase(fetchAllAllocationByMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // FULL DAY CASES
      .addCase(fetchAllFullDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFullDay.fulfilled, (state, action) => {
        state.loading = false;
        state.allFullDayCases = action.payload;
      })
      .addCase(fetchAllFullDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // LAB WISE CASES
    builder
      .addCase(fetchLabWiseCasesByMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabWiseCasesByMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.labWiseCases = action.payload;
      })
      .addCase(fetchLabWiseCasesByMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // LAB WISE REDESIGNS
    builder
      .addCase(fetchLabWiseRedesignsByMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabWiseRedesignsByMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.labWiseRedesigns = action.payload;
      })
      .addCase(fetchLabWiseRedesignsByMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchEmployeeUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeUnits = action.payload;
      })
      .addCase(fetchEmployeeUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchMarkedUnitsExport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarkedUnitsExport.fulfilled, (state, action) => {
        state.loading = false;
        state.markedUnits = action.payload;
      })
      .addCase(fetchMarkedUnitsExport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchDailyRedesigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyRedesigns.fulfilled, (state, action) => {
        state.loading = false;
        state.dailyRedesigns = action.payload;
      })
      .addCase(fetchDailyRedesigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchLiveProductivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLiveProductivity.fulfilled, (state, action) => {
        state.loading = false;
        state.liveProductivity = action.payload;
      })
      .addCase(fetchLiveProductivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchRdPercentage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRdPercentage.fulfilled, (state, action) => {
        state.loading = false;
        state.rdPercentage = action.payload;
      })
      .addCase(fetchRdPercentage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchCasesCame.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCasesCame.fulfilled, (state, action) => {
        state.loading = false;
        state.casesCame = action.payload;
      })
      .addCase(fetchCasesCame.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchBillingDataByMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingDataByMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.billingData = action.payload;
      })
      .addCase(fetchBillingDataByMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchCaseLifecycleReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCaseLifecycleReport.fulfilled, (state, action) => {
        state.loading = false;
        state.caseLifecycleReport = action.payload;
      })
      .addCase(fetchCaseLifecycleReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchCaseLifecycleHistory.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchCaseLifecycleHistory.fulfilled, (state, action) => {
        state.caseLifecycleHistory = action.payload;
      })
      .addCase(fetchCaseLifecycleHistory.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default dataExtractSlice.reducer;